
import { Timestamp } from '@firebase/firestore';
import { CommissionTier } from './types/commissionTier';

export type { CommissionTier };

export enum UserRole {
  SUPERADMIN = 'SUPERADMIN', // Renamed from ADMINISTRADOR
  ADMIN = 'ADMIN', // Added new ADMIN role
  OPERACION = 'OPERACION', // Nuevo rol para gestión operativa
  BROKER = 'BROKER',
  BROKER_INTERNO = 'BROKER_INTERNO', // New role
  BROKER_PREMIUM = 'BROKER_PREMIUM', // New role
  BROKER_EXTERIOR = 'BROKER_EXTERIOR', // New role
  CLIENTE = 'CLIENTE', // New role
  COMERCIAL_INTERNO = 'COMERCIAL_INTERNO', // New role
  BROKERS_LEADS = 'BROKERS_LEADS', // New role for brokers focused on leads
  SIN_ROL = 'SIN_ROL',
}

export enum UserStatus {
  ACTIVE = 'Activo',
  INACTIVE = 'Inactivo',
  PENDING = 'Pendiente',
}

export interface Property {
  // Core fields
  id: string; // Firestore document ID
  codigo: string; // Main property code
  codigo_enmascarado?: string; // Masked code for Premium Brokers
  
  // Basic Info
  ciudad: string; 
  tipoinmueble: string; // Mapped from 'tipo_inmueble'
  direccion: string; 
  descripcion: string; 
  precio_venta: number;
  valor_administracion: number; 
  commissionRate: number; // Keep for compatibility/calculations
  estrato: number; 
  
  // Detailed characteristics
  area_construida_original: string; // e.g., "100 m²" or number as string
  area_construida?: number; // Numeric field for comparisons
  area_privada: string | null; 
  num_habitaciones: number; 
  num_banos: number; 
  num_parqueaderos: number; 
  num_balcones?: number; // New field
  
  // Links and Status
  images: string[]; // Mapped from 'imagenes'
  technicalSheetUrl: string; 
  photosZipUrl: string; 
  brochureUrl: string; 
  
  // Status Logic Update:
  // 'Activo' | 'Inactivo' | 'Eliminado' | 'ActualizadoCodigo'
  status: string; 
  
  owner: string; 
  features: string[]; // Array of string features
  tag?: string[]; // Lista de tags del inmueble
  tags?: string[]; // Alias para tags del inmueble
  video_youtube_url?: string | null; 
  path?: string | null; 

  // Location
  countryName?: string | null;
  neighborhoodName?: string | null; // Mapped from 'nombre_barrio'
  latitude?: number | null;
  longitude?: number | null;
  localidad?: string | null; 

  // Details
  currencyCode?: string | null; 
  constructionYear?: number | null; // Mapped from 'anio_construccion'
  remodelationYear?: number | null; 
  propertyCondition?: string | null; 
  vigilanceType?: string | null; // Mapped from 'tipo_vigilancia'
  antiguedad?: number | null; // Calculated or legacy
  estrategiaComercial?: string | null; 
  ascensor?: number | null; 
  num_ascensores?: number | string | null; 
  
  // New detailed property fields
  codigo_brp?: string | null; // Mapped from 'codigo_brp_o_inmobiliaria'
  piso?: number | string | null;
  numero_predial?: string | null;
  numero_matricula?: string | null;
  es_visible_broker?: boolean; // Controls visibility for standard brokers

  // --- NUEVOS CAMPOS PARA CREACIÓN MANUAL ---
  es_preventa?: boolean;
  visible_roles?: UserRole[];
  creado_manualmente?: boolean;
  usuario_creador?: string;

  // --- CAMPOS GESTIÓN INTERNA (MAPEO CORREGIDO) ---
  nombre_cliente?: string | null;    // Mapped to: nombre_contacto
  telefono_cliente?: string | null;  // Mapped to: celular_contacto
  correo_1?: string | null;          // Mapped to: correo_contacto
  
  nombre_encargado?: string | null;
  telefono_encargado?: string | null;
  disponibilidad_visitas?: string | null;
  observaciones_internas?: string | null;

  // --- COMISION OVERRIDES ---
  comision_override_broker?: number | null;
  comision_override_premium?: number | null;
  comision_override_interno?: number | null;
  tiene_oferta?: boolean;
  valor_oferta?: number | null;
  tabla_comisiones?: CommissionTier[] | null;

  // Legacy/Transient fields (kept for compatibility with existing components)
  es_eliminado?: boolean; // Derived from status === 'Eliminado'
  es_actualizado?: boolean; // Derived from status === 'ActualizadoCodigo'
  codigo_actual?: string;
  codigo_anterior?: string;
  fue_actualizado?: boolean;
  fechaAgregadoNuevo?: Timestamp | { seconds: number; nanoseconds?: number } | string | null;
  fecha_agregado_nuevo?: Timestamp | { seconds: number; nanoseconds?: number } | string | null;
  fecha_creacion?: Timestamp | { seconds: number; nanoseconds?: number } | string | null;
  
  // Additional API mapping fields (kept as optional)
  idRealEstate?: number | null;
  idTransactionType?: number | null;
  transactionType?: string | null;
  idPropertyType?: number | null;
  idCity?: number | null;
  idCountry?: number | null;
  idNeighborhood?: number | null;
  idSector?: number | null;
  sectorName?: string | null;
  idCurrency?: number | null;
  idPropertyCondition?: number | null;
  idVigilanceType?: number | null;
}

export interface City {
  id: string;
  name: string;
  count?: number;
}

export interface PropertyType {
  id: string;
  name: string;
  count?: number;
}

export interface SearchFilters {
  propertyCode: string; 
  city: string;
  propertyType: string;
}

export interface AppUser {
  id: string;
  uid: string;
  email: string | null;
  displayName: string | null;
  username: string;
  photoURL: string | null;
  role: UserRole;
  status: UserStatus;
  createdAt: Timestamp;
  lastLoginAt: Timestamp;
  onboardingCompleted?: boolean;
  zohoOnboardingCompleted?: boolean;
  commercializationCities?: string[];
  commercializationZones?: { [city: string]: string[] };
  priceRangeMin?: number | null;
  priceRangeMax?: number | null;
  nombreCompleto?: string;
  celular?: string;
  numeroIdentificacion?: string;
  tipoIdentificacion?: string; // New field
  carnetConfigurado?: boolean; // New field
  complementoCelular?: string;
  recordarInformacion?: boolean;
  sourceDB?: 'comercial' | 'broker';
  id_zoho?: string;
  revision_link_nuevos?: any;
  allowedOwners?: string[] | string;
}

export type User = AppUser;

export interface AppConfigParameter {
  id: string;
  name: string;
  enabled?: boolean;
  value?: string | number | object;
  current_version?: string;
}

export interface AppVersionInfoConfig extends AppConfigParameter {
  id: 'app_version_info';
  current_version: string;
  force_update_below?: string;
}

export interface LoginGoogleEnabledConfig extends AppConfigParameter {
  id: 'login_google_enabled';
  enabled: boolean;
}

export interface LoginEmailPasswordEnabledConfig extends AppConfigParameter {
  id: 'login_email_password_enabled';
  enabled: boolean;
}

export interface InterestRatesValue {
  visThreshold: number;
  credito: {
    vis: number;
    noVis: number;
  };
  leasing: {
    vis: number;
    noVis: number;
  };
}

export interface InterestRatesConfig extends AppConfigParameter {
  id: 'interest_rates';
  value: InterestRatesValue;
}

export interface VisibilityMatrixValue {
  [role: string]: string[]; // role -> array of allowed owners
}

export interface VisibilityMatrixConfig extends AppConfigParameter {
  id: 'visibility_matrix';
  value: VisibilityMatrixValue;
}

export type AppConfig = {
  [key: string]: AppConfigParameter;
  login_google_enabled?: LoginGoogleEnabledConfig;
  login_email_password_enabled?: LoginEmailPasswordEnabledConfig;
  app_version_info?: AppVersionInfoConfig;
  interest_rates?: InterestRatesConfig;
  visibility_matrix?: VisibilityMatrixConfig;
};

// ... Ciencuadras API interfaces ...
export interface CiencuadrasApiImageData {
    physicalSpace?: string;
    id_photo?: string;
    type?: string;
    isNormalized?: string;
    formatted_photo?: string;
    url?: string;
    is360?: string;
    order?: string;
}

export interface CiencuadrasApiContactData {
    number?: string;
    isVisible?: string;
    type?: string;
    typeAction?: string;
}

export interface CiencuadrasApiLocationData {
    lon?: string;
    lat?: string;
}

export interface CiencuadrasApiResponseData {
    id?: number;
    codigo?: string;
    contacto_whatsapp?: string;
    num_balcones?: string;
    tiene_tiendas_barrio?: number;
    path?: string;
    area_bodega?: number | null;
    feria_vivienda_descripcion?: string | null;
    contacto_fijo?: string | null;
    tiene_estacion_transmilenio?: number;
    prioritario?: number;
    tiene_estudio?: number;
    longitud?: number;
    tipo_parqueadero?: string | null;
    colombiano_exterior?: number;
    area?: number;
    barrio_usuario?: string | null;
    id_barrio?: number;
    oferta_brp?: number;
    area_desde_original?: number;
    precio_venta?: number;
    barrio_integrador?: string;
    incluye_servicios_publicos?: number;
    id_tipo_transaccion?: number;
    zona_lavanderia?: number;
    id_localidad_integrador?: number;
    correo_usuario?: string;
    tiene_hospitales?: number;
    amoblado?: number;
    nombre_proyecto?: string | null;
    horno?: number;
    tiene_cocina_integral?: number;
    cinta_tarjeta?: string | null;
    num_depositos?: number | null;
    id_tipo_inmueble?: number;
    area_desde?: number | null;
    destacadopagina?: number;
    num_habitaciones?: number;
    area_construida_original?: number;
    lavadora?: number;
    zonacompare?: string | null;
    enoferta?: number;
    area_construida?: number;
    tiene_jardines?: number;
    piscina_privada?: number;
    id_usuario?: number;
    num_ascensores?: string;
    es_sin_mercantil?: number;
    precio_leasing_habitacional?: number;
    activo?: string;
    tiene_terraza?: number;
    recepcion?: number;
    tiene_porteria?: number;
    num_parqueaderos?: number;
    id_depto?: number;
    barrio_integrador_compare?: string;
    antiguedad?: number;
    es_360_pn?: number;
    tipoinmueble?: string;
    valor_administracion?: number;
    zonas_verdes?: number;
    oferta_mercantil?: number;
    permite_mascotas?: number;
    tiene_vigilancia?: number;
    destacado?: number;
    tiene_conjunto_cerrado?: number;
    estado?: string;
    fecha_creacion?: string;
    foto_formateado?: number;
    barrio_compare?: string;
    feria_vivienda_cinta?: string | null;
    tiene_gimnasios?: number;
    cuarto_servicio?: number;
    localidad_integrador?: string;
    url_portal?: string;
    tiene_supermercados?: number;
    luz?: string;
    id_localidad?: number;
    tipotransaccion?: string;
    vigilancia?: string | null;
    gimnasio?: number;
    ciudad?: string;
    latitud?: number;
    direccion?: string;
    canon_arrendamiento?: number;
    tiene_aire_acondicionado?: number;
    piscina_comunal?: number;
    tiene_balcon?: number;
    feria_vivienda?: number;
    url_foto?: string;
    enoferta_especial?: number;
    chimenea?: number;
    ocultar_direccion?: number;
    proyecto?: number;
    descripcion?: string;
    zona_infantil?: number;
    estufa?: string;
    imagen?: string;
    tiene_coworking?: number;
    area_oficina?: number | null;
    area_privada?: number | null;
    num_terraza?: string;
    barrio?: string;
    departamento?: string;
    localidad_compare?: string;
    es_mercantil_360?: number;
    tiene_patio?: number;
    area_lote?: number | null;
    tiene_fotos?: number;
    tiene_centros_comerciales?: number;
    num_banos?: number;
    tipo_propietario?: string;
    foto_normalizado?: number;
    nombre_usuario?: string;
    id_tipo_usuario?: number;
    destacado_resultados?: number;
    localidad?: string;
    imagenes?: CiencuadrasApiImageData[];
    certificado?: number;
    destacado_home?: number;
    localidad_integrador_compare?: string;
    contacto_llamada?: string;
    es_mercantil_foto?: number;
    precio_credito_hipotecario?: number;
    matterport?: number;
    lista_contactos?: CiencuadrasApiContactData[];
    canon_arrendamiento_original?: number;
    ascendido?: number;
    pais?: string;
    ciudad_compare?: string;
    localizacion?: CiencuadrasApiLocationData;
    tiene_deposito?: number;
    id_ciudad?: number;
    barrio_usuario_compare?: string | null;
    bannerpagina?: number;
    salon_social?: number;
    tiene_bbq?: number;
    ensubasta?: number;
    tiene_parques?: number;
    estado_proyecto?: string | null;
    precio_desde_original?: number;
    id_barrio_integrador?: number;
    es_360?: number;
    descripcion_oferta?: string | null;
    sede_social?: number;
    precio_venta_original?: number;
    departamento_compare?: string;
    fecha_modificacion?: string;
    estrato?: string;
    es_foto_pn?: number;
    _weight?: number;
    _group?: number;
    video_youtube_url?: string | null;
    realEstateCode?: string; 
    realEstateRegistration?: string | null;
    propertyAddress?: string;
    id_pais?: number | null; 
    realEstateMultimedia?: { 
        idRealEstateMultimedia?: number;
        idMultimediaType?: number;
        multimediaType?: string;
        url?: string;
        principal?: boolean;
    }[];
    realEstateFeatures?: { 
        idRealEstateFeature?: number;
        idFeature?: number;
        featureName?: string;
        value?: string; 
    }[];
}

export interface CiencuadrasApiResponse {
    code?: number;
    message?: string;
    data?: CiencuadrasApiResponseData;
    delete?: boolean;
    success?: boolean;
    pathUrl?: string | null;
}

export interface CsvRowProcessResult {
  originalRowNumber: number;
  originalPropertyCode: string;
  originalCommissionRate: string;
  estrategiaComercial?: string;
  status: 'Éxito' | 'Fallido' | 'Advertencia' | 'Omitido';
  details: string;
  firestoreId?: string;
}

export interface UserCsvRowProcessResult {
  originalRowNumber: number;
  email: string;
  status: 'Éxito' | 'Fallido' | 'Advertencia' | 'Omitido';
  details: string;
  firestoreId?: string;
}
